import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import "./StudentForm.css";
import AvailableTimesDialog from "./schedule/AvailableTimesDialog.js";
import FilterToolbar from "./FilterToolbar.js";
import LoginHistoryModal from "./LoginHistoryModal.js";
import { API_URL } from "../config/api.js";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filters, setFilters] = useState({
    assignmentStatus: "all",
    ageGroup: "all",
    teamPlayer: "all",
    trainigGroup: "all",
    skillLevel: "all",
  });
  const [editingAvailableTimes, setEditingAvailableTimes] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);

  // Pagination state - restore from localStorage
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('studentFormPage');
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const studentsPerPage = 40;
  const [editedStudent, setEditedStudent] = useState({
    firstName: "",
    lastName: "",
    skillLevel: "",
    sex: "",
    availableTimes: "",
    adress: "",
    email: "",
    phone: "",
    member: true,
    adult: true,
    team: false,
    trainigGroup: "",
    groupSize: "",
    birthDate: "",
    comment: "",
    coach: "",
    frequence: "",
  });

  useEffect(() => {
    fetchStudents();
    fetchCoaches();
    fetchSchedule();
    fetchUsers();
  }, []);

  // Save current page to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('studentFormPage', currentPage.toString());
  }, [currentPage]);

  // Restore selected student from localStorage on mount
  useEffect(() => {
    if (students.length > 0) {
      const savedStudentId = localStorage.getItem('selectedStudentId');
      if (savedStudentId) {
        const student = students.find(s => s._id === savedStudentId);
        if (student) {
          setSelectedStudent(student._id);
          setEditedStudent({ ...student });
        } else {
          // Student was deleted, silently clear
          localStorage.removeItem('selectedStudentId');
        }
      }
    }
  }, [students]);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students`);
      setStudents(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Schüler:", err);
    }
  };

  const fetchCoaches = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/coaches`);
      setCoaches(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Trainer:", err);
    }
  };

  const fetchSchedule = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule`);
      setSchedule(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen des Zeitplans:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setUsers(res.data);
    } catch (err) {
      console.error("Fehler beim Abrufen der Benutzer:", err);
    }
  };

  // Calculate course statistics
  const courseStats = React.useMemo(() => {
    // Debug logging
    console.log('[StudentForm] Calculating course stats:');
    console.log('  - schedule length:', schedule?.length || 0);
    console.log('  - coaches length:', coaches?.length || 0);

    // Debug: Check first few schedule entries
    if (schedule && schedule.length > 0) {
      console.log('  - First 5 schedule entries:');
      schedule.slice(0, 5).forEach((course, i) => {
        console.log(`    [${i}] ${course.day} ${course.hour}: students =`,
          Array.isArray(course.students) ? course.students : typeof course.students,
          'length =', course.students?.length || 0);
      });
    }

    // Count courses with at least 1 student
    const occupiedCourses = schedule.filter(
      (course) => course.students && course.students.length > 0
    ).length;

    console.log('  - Filtered occupied courses:', occupiedCourses);

    // Count all possible courses (each coach-time combination)
    let possibleCourses = 0;
    coaches.forEach(coach => {
      if (Array.isArray(coach.availableTimes)) {
        console.log(`  - Coach ${coach.firstName} ${coach.lastName}: ${coach.availableTimes.length} slots`);
        possibleCourses += coach.availableTimes.length;
      } else {
        console.log(`  - Coach ${coach.firstName} ${coach.lastName}: availableTimes is NOT an array:`, typeof coach.availableTimes);
      }
    });

    console.log('  - RESULT: occupiedCourses =', occupiedCourses, ', possibleCourses =', possibleCourses);

    return {
      occupiedCourses,
      possibleCourses
    };
  }, [schedule, coaches]);

  // Helper function to get coach name by ID or name
  const getCoachName = (coachValue) => {
    if (!coachValue) return null;

    // Try to find coach by ID
    const coach = coaches.find(c => String(c._id) === String(coachValue));
    if (coach) {
      return `${coach.firstName} ${coach.lastName}`;
    }

    // If not found by ID, assume it's already a name (legacy format)
    return coachValue;
  };

  // Helper function to find user linked to selected student
  const getLinkedUser = () => {
    if (!selectedStudent) return null;

    const linkedUser = users.find(u =>
      u.studentId && String(u.studentId) === String(selectedStudent)
    );

    return linkedUser || null;
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setFilters({
      assignmentStatus: "all",
      ageGroup: "all",
      teamPlayer: "all",
      trainigGroup: "all",
      skillLevel: "all",
    });
    setSearchQuery("");
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student._id);
    setEditedStudent({
      ...student,
    });
    // Save selected student to localStorage
    localStorage.setItem('selectedStudentId', student._id);
  };

  const handleSaveAvailableTimes = (selectedTimes) => {
    setEditedStudent((prev) => ({
      ...prev,
      availableTimes: selectedTimes,
    }));

    // Optional: API-Aufruf, um die Daten zu speichern
    /*axios
      .put(`${API_URL}/api/students/${studentId}`, {
        availableTimes: selectedTimes,
      })
      .catch((err) =>
        console.error("Fehler beim Speichern der Trainingszeiten:", err)
      );*/

    setEditingAvailableTimes(false); // Dialog schließen
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedStudent((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/students/${selectedStudent}`, {
        ...editedStudent,
        availableTimes: editedStudent.availableTimes,
      });
      fetchStudents();
      toast.success("Schüler erfolgreich aktualisiert!");
      setSelectedStudent(null);
      // Clear from localStorage after save
      localStorage.removeItem('selectedStudentId');
    } catch (err) {
      console.error("Fehler beim Speichern:", err);
      const errorMessage = err.response?.data?.error || "Fehler beim Speichern der Änderungen";
      toast.error(errorMessage);
    }
  };

  // Handle Enter key press to save
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevent default only if not in a textarea or specific elements
      if (e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'BUTTON') {
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Möchten Sie diesen Schüler wirklich löschen?")) return;
    try {
      await axios.delete(`${API_URL}/api/students/${id}`);
      fetchStudents();
      if (selectedStudent === id) {
        setSelectedStudent(null);
        setEditedStudent({
          firstName: "",
          lastName: "",
          dateOfBirth: "",
          skillLevel: "",
          availableTimes: "",
          member: false, // Zurücksetzen auf false
          adult: false,
          team: false,
          trainigGroup: "",
          groupSize: "",
          birthDate: "",
          comment: "",
          adress: "",
          email: "",
          phone: "",
          coach: "",
          frequence: "",
        });
        // Clear from localStorage
        localStorage.removeItem('selectedStudentId');
      }
    } catch (err) {
      console.error("Fehler beim Löschen:", err);
    }
  };

  const filteredStudents = students
    .filter((student) => {
      // Search filter
      const matchesSearch = `${student.firstName} ${student.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      // Assignment status filter
      if (filters.assignmentStatus !== "all") {
        const isAssigned = student.day && student.hour !== null && student.hour !== undefined;
        if (filters.assignmentStatus === "assigned" && !isAssigned) return false;
        if (filters.assignmentStatus === "unassigned" && isAssigned) return false;
      }

      // Age group filter
      if (filters.ageGroup !== "all") {
        if (filters.ageGroup === "adult" && !student.adult) return false;
        if (filters.ageGroup === "children" && student.adult) return false;
      }

      // Team player filter (only for children)
      if (filters.teamPlayer !== "all" && !student.adult) {
        if (filters.teamPlayer === "yes" && !student.team) return false;
        if (filters.teamPlayer === "no" && student.team) return false;
      }

      // Training group filter (only for children) - when selected, exclude all adults
      if (filters.trainigGroup !== "all") {
        if (student.adult) return false; // Exclude adults when training group filter is active
        if (student.trainigGroup !== filters.trainigGroup) return false;
      }

      // Skill level filter (only for adults) - when selected, exclude all children
      if (filters.skillLevel !== "all") {
        if (!student.adult) return false; // Exclude children when skill level filter is active
        if (student.skillLevel !== filters.skillLevel) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const lastNameA = (a.lastName || "").toLowerCase();
      const lastNameB = (b.lastName || "").toLowerCase();
      return lastNameA.localeCompare(lastNameB);
    });

  // Calculate children and adult counts
  const childrenCount = students.filter(s => !s.adult).length;
  const adultsCount = students.filter(s => s.adult).length;

  // Pagination calculations
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = filteredStudents.slice(indexOfFirstStudent, indexOfLastStudent);

  // Generate page numbers for pagination controls
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Add ellipsis before range if needed
      if (startPage > 2) {
        pages.push('...');
      }

      // Add pages around current page
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis after range if needed
      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="student-form-container">
      <div className="student-form-header">
        <h1>👥 Schülerverwaltung</h1>
      </div>

      <FilterToolbar
        filters={filters}
        onFilterChange={handleFilterChange}
        onReset={handleResetFilters}
        totalCount={students.length}
        filteredCount={filteredStudents.length}
        childrenCount={childrenCount}
        adultsCount={adultsCount}
        occupiedCourses={courseStats.occupiedCourses}
        possibleCourses={courseStats.possibleCourses}
      />

      <div
        className={`student-form-content ${
          selectedStudent ? "has-selection" : ""
        }`}
      >
        <div className="student-list-card">
          <h2>Schülerliste</h2>
          <input
            type="text"
            className="search-box"
            placeholder="🔍 Schüler suchen..."
            value={searchQuery}
            onChange={handleSearch}
          />

          <div className="student-list">
            {currentStudents.map((student) => (
              <div
                className={`student-item ${
                  selectedStudent?._id === student._id ? "selected" : ""
                }`}
                key={student._id}
                onClick={() => handleSelectStudent(student)}
              >
                <span className="student-name">
                  {student.firstName} {student.lastName}{" "}
                  {student.skillLevel ? `• ${student.skillLevel}` : ""}
                </span>
                <button
                  className="delete-button"
                  title="Löschen"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(student._id);
                  }}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                ← Zurück
              </button>

              <div className="pagination-numbers">
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Weiter →
              </button>
            </div>
          )}
        </div>

        {selectedStudent && (
          <div className="edit-form-card" onKeyDown={handleKeyDown}>
            <h3 className="form-heading">Schüler bearbeiten</h3>
            {/* Dialog zum Bearbeiten der Trainingszeiten */}
            {editingAvailableTimes && (
              <AvailableTimesDialog
                entity={editedStudent}
                closeDialog={() => setEditingAvailableTimes(false)}
                handleSaveAvailableTimes={handleSaveAvailableTimes}
              />
            )}
            {/* Labels über den Eingabefeldern */}
            <div className="form-field">
              <label htmlFor="firstName">Vorname</label>
              <input
                autoFocus
                id="firstName"
                type="text"
                name="firstName"
                value={editedStudent.firstName || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="lastName">Nachname</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                value={editedStudent.lastName || ""}
                onChange={handleEditChange}
              />
            </div>

            <div className="form-field">
              <label htmlFor="availableTimes">Verfügbare Zeiten</label>
              <button onClick={() => setEditingAvailableTimes(true)}>
                Trainingszeiten bearbeiten
              </button>
            </div>

            {/* Current Assignment Display (Read-only) */}
            <div className="form-field">
              <label>Aktuelle Zuweisung</label>
              <div style={{
                padding: '10px',
                backgroundColor: '#f0f8ff',
                borderRadius: '4px',
                border: '1px solid #cce7ff'
              }}>
                {Array.isArray(editedStudent.assignments) && editedStudent.assignments.length > 0 ? (
                  // NEW FORMAT: Show all assignments from array
                  <>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Zuweisungen ({editedStudent.assignments.length}):</strong>
                    </div>
                    {editedStudent.assignments.map((assignment, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginTop: idx > 0 ? '10px' : '0',
                          paddingTop: idx > 0 ? '10px' : '0',
                          borderTop: idx > 0 ? '1px solid #e0e0e0' : 'none'
                        }}
                      >
                        <div>📅 <strong>{assignment.day}</strong> um <strong>{assignment.hour}:00 Uhr</strong></div>
                        {assignment.coach && (
                          <div style={{ marginTop: '4px' }}>
                            👨‍🏫 {getCoachName(assignment.coach) || 'Nicht zugewiesen'}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  // LEGACY FORMAT: Fall back to single assignment fields
                  editedStudent.day && editedStudent.hour ? (
                    <>
                      <div><strong>Tag & Zeit:</strong> {editedStudent.day} um {editedStudent.hour}:00 Uhr</div>
                      {editedStudent.coach && (
                        <div><strong>Trainer:</strong> {getCoachName(editedStudent.coach) || 'Nicht zugewiesen'}</div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: '#666' }}>Noch keinem Kurs zugewiesen</div>
                  )
                )}
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                  ℹ️ Änderungen über den Trainingsplan vornehmen
                </div>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="adress">Adresse</label>
              <input
                id="adress"
                type="text"
                name="adress"
                value={editedStudent.adress || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="birthDate">Geburtsdatum</label>
              <input
                id="birthDate"
                type="text"
                name="birthDate"
                placeholder="YYYY-MM-DD"
                value={editedStudent.birthDate || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="phone">Telefonnummer</label>
              <input
                id="phone"
                type="text"
                name="phone"
                value={editedStudent.phone || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="email">E-Mail</label>
              <input
                id="email"
                type="text"
                name="email"
                value={editedStudent.email || ""}
                onChange={handleEditChange}
              />
            </div>
            <div className="form-field">
              <label htmlFor="member">Mitglied</label>
              {/* Toggle-Switch statt Radiobuttons: 
                - Checkbox verwaltet Boolean direkt 
                - Bei checked: "Ja", sonst "Nein" */}
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="member"
                  name="member"
                  checked={editedStudent.member}
                  onChange={(e) =>
                    setEditedStudent((prev) => ({
                      ...prev,
                      member: e.target.checked,
                    }))
                  }
                />
                <span className="toggle-label">
                  {editedStudent.member ? "Ja" : "Nein"}
                </span>
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="adult">Erwachsenen-Training</label>
              {/* Toggle-Switch statt Radiobuttons: 
                - Checkbox verwaltet Boolean direkt 
                - Bei checked: "Ja", sonst "Nein" */}
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="adult"
                  name="adult"
                  checked={editedStudent.adult}
                  onChange={(e) =>
                    setEditedStudent((prev) => ({
                      ...prev,
                      adult: e.target.checked,
                    }))
                  }
                />
                <span className="toggle-label">
                  {editedStudent.adult ? "Ja" : "Nein"}
                </span>
              </div>
            </div>
            {!editedStudent.adult && (
              <div className="form-field">
                <label htmlFor="team">Teamspieler</label>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    id="team"
                    name="team"
                    checked={editedStudent.team}
                    onChange={(e) =>
                      setEditedStudent((prev) => ({
                        ...prev,
                        team: e.target.checked,
                      }))
                    }
                  />
                  <span className="toggle-label">
                    {editedStudent.team ? "Ja" : "Nein"}
                  </span>
                </div>
              </div>
            )}
            {editedStudent.adult && (
              <>
                <div className="form-field">
                  <label htmlFor="skillLevel">Spielstärke</label>
                  <select
                    id="skillLevel"
                    name="skillLevel"
                    type="text"
                    value={editedStudent.skillLevel || ""}
                    onChange={(e) => {
                      //console.log("Ausgewählter Wert:", e.target.value); // Überwache den Wert
                      setEditedStudent((prev) => ({
                        ...prev,
                        skillLevel: e.target.value, // Aktualisiert den Zustand
                      }));
                    }}
                  >
                    <option value="">Bitte wählen</option>
                    <option value="Anfänger">Anfänger</option>
                    <option value="Anfänger mit Grundkenntnissen">
                      Anfänger mit Grundkenntnissen
                    </option>
                    <option value="Fortgeschritten">Fortgeschritten</option>
                    <option value="Erfahrene Spieler:innen / Mannschaftsspieler:innen">
                      Erfahrene Spieler:innen / Mannschaftsspieler:innen
                    </option>
                    <option value="Leistungsspieler:innen / Turnierspieler:innen">
                      Leistungsspieler:innen / Turnierspieler:innen
                    </option>
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="sex">Geschlecht</label>
                  <select
                    id="sex"
                    name="sex"
                    value={editedStudent.sex || ""}
                    onChange={(e) => {
                      setEditedStudent((prev) => ({
                        ...prev,
                        sex: e.target.value,
                      }));
                    }}
                  >
                    <option value="">Bitte wählen</option>
                    <option value="männlich">männlich</option>
                    <option value="weiblich">weiblich</option>
                  </select>
                </div>
              </>
            )}
            {/* Nur wenn „Erwachsenen-Training“ deaktiviert ist, Gruppe wählen */}
            {!editedStudent.adult && (
              <div className="form-field">
                <label htmlFor="trainigGroup">Kinder-Gruppe</label>
                <select
                  id="trainigGroup"
                  name="trainigGroup"
                  value={editedStudent.trainigGroup}
                  onChange={(e) =>
                    setEditedStudent((prev) => ({
                      ...prev,
                      trainigGroup: e.target.value,
                    }))
                  }
                >
                  <option value="">Bitte wählen</option>
                  <option value="Kinderland">Kinderland</option>
                  <option value="Rot">Rot</option>
                  <option value="Grün">Grün</option>
                  <option value="Orange">Orange</option>
                  <option value="Gelb Team">Gelb Team</option>
                  <option value="Gelb Hobby">Gelb Hobby</option>
                </select>
              </div>
            )}

            <div className="form-field">
              <label htmlFor="frequence">Trainigshäufigkeit</label>
              <select
                id="frequence"
                name="frequence"
                type="text"
                value={editedStudent.frequence}
                onChange={(e) => {
                  //console.log("Ausgewählter Wert:", e.target.value); // Überwache den Wert
                  setEditedStudent((prev) => ({
                    ...prev,
                    frequence: e.target.value, // Aktualisiert den Zustand
                  }));
                }}
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="comment">Notiz</label>
              <input
                id="comment"
                type="text"
                name="comment"
                value={editedStudent.comment || ""}
                onChange={handleEditChange}
              />
            </div>
            {/* Weitere Eingabefelder bleiben unverändert */}
            <div className="form-actions">
              <button className="btn-primary" onClick={handleSave}>
                💾 Speichern
              </button>
            </div>

            {/* Login History Button - only show if student has linked user account */}
            {getLinkedUser() && (
              <div className="login-history-section">
                <button
                  className="btn-secondary"
                  onClick={() => setShowLoginHistory(true)}
                  style={{ width: '100%', marginTop: '10px' }}
                >
                  📜 Login-Historie anzeigen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Login History Modal */}
      {showLoginHistory && getLinkedUser() && (
        <LoginHistoryModal
          userId={getLinkedUser()._id}
          userName={`${editedStudent.firstName} ${editedStudent.lastName}`}
          onClose={() => setShowLoginHistory(false)}
        />
      )}
    </div>
  );
};

export default StudentManagement;
